'use client';
import { Client } from "@microsoft/microsoft-graph-client";
import { list } from 'postcss';

interface AlertFields {
  Title: string;
  Message: string;
  EndDateTime: string | number | Date;
  Severity: string;
}

interface AlertResponse {
  value: { fields: AlertFields }[];
}

let listId: string | undefined = '';
let siteId: string | undefined = '';
let msalRedirectUri: string | undefined = '';
let msalPostLogoutRedirectUri: string | undefined = '';

if (process.env.NODE_ENV?.toLowerCase() === "development") {
  listId = process.env.NEXT_PUBLIC_SHAREPOINT_LIST_ID_DEV;
  siteId = process.env.NEXT_PUBLIC_SHAREPOINT_SITE_ID_DEV;
  msalRedirectUri = process.env.NEXT_PUBLIC_MSAL_REDIRECT_URI_DEV;
  msalPostLogoutRedirectUri = process.env.NEXT_PUBLIC_MSAL_POST_LOGOUT_REDIRECT_URI_DEV;
  console.log("Using Local Development SharePoint configuration");
} else if (typeof window !== "undefined") {
  const href = window.location.href.toLowerCase();
  if (href.includes("gchelp-dev")) {
    listId = process.env.NEXT_PUBLIC_SHAREPOINT_LIST_ID_DEV;
    siteId = process.env.NEXT_PUBLIC_SHAREPOINT_SITE_ID_DEV;
    msalRedirectUri = process.env.NEXT_PUBLIC_MSAL_REDIRECT_URI_DEV;
    msalPostLogoutRedirectUri = process.env.NEXT_PUBLIC_MSAL_POST_LOGOUT_REDIRECT_URI_DEV;
    console.log("Using DEV SharePoint configuration");
  } else if (href.includes("gchelp-uat")) {
    listId = process.env.NEXT_PUBLIC_SHAREPOINT_LIST_ID_UAT;
    siteId = process.env.NEXT_PUBLIC_SHAREPOINT_SITE_ID_UAT;
    msalRedirectUri = process.env.NEXT_PUBLIC_MSAL_REDIRECT_URI_UAT;
    msalPostLogoutRedirectUri = process.env.NEXT_PUBLIC_MSAL_POST_LOGOUT_REDIRECT_URI_UAT;
    console.log("Using UAT SharePoint configuration");
  } else {
    listId = process.env.NEXT_PUBLIC_SHAREPOINT_LIST_ID_PROD;
    siteId = process.env.NEXT_PUBLIC_SHAREPOINT_SITE_ID_PROD;
    msalRedirectUri = process.env.NEXT_PUBLIC_MSAL_REDIRECT_URI_PROD;
    msalPostLogoutRedirectUri = process.env.NEXT_PUBLIC_MSAL_POST_LOGOUT_REDIRECT_URI_PROD;
    console.log("Using PROD SharePoint configuration");
  }
} else {
  // Fallback for SSR or unknown environment
  listId = process.env.NEXT_PUBLIC_SHAREPOINT_LIST_ID_PROD;
  siteId = process.env.NEXT_PUBLIC_SHAREPOINT_SITE_ID_PROD;
  msalRedirectUri = process.env.NEXT_PUBLIC_MSAL_REDIRECT_URI_PROD;
  msalPostLogoutRedirectUri = process.env.NEXT_PUBLIC_MSAL_POST_LOGOUT_REDIRECT_URI_PROD;
  console.log("Using PROD SharePoint configuration (SSR fallback)");
}

export const getAuthenticatedGraphClient = async (accessToken: string) =>
  Client.init({
    authProvider: (done) => {
      done(null, accessToken);
    },
  });

export const fetchAlerts = async (accessToken: string) => {
  try {
    if (!accessToken) {
      throw new Error("Failed to acquire access token");
    }

    const client = await getAuthenticatedGraphClient(accessToken);

    if (!client) {
      throw new Error("Failed to initialize Graph client");
    }

    const columnNames = ["Title", "Message", "Severity", "EndDateTime"];
    const response: AlertResponse = await client
      .api(
        `/sites/${siteId}/lists/${listId}/items?expand=fields(select=${columnNames.join(",")})`,
      )
      .get();

    if (!response || !response.value) {
      throw new Error("Invalid response from Graph API");
    }

    return response.value.map(({ fields }) => fields);
  } catch (error) {
    console.error("Error fetching alerts from SharePoint:", error);
    throw error;
  }
};